import { ListingSchemaView } from "shared/app/ListingTemplate/slices/types";
import {
  ListingButtons,
  ListingPricePrimaryDisplay,
  ListingPriceSecondaryDisplay,
  PrescreeningMode,
  PrescreeningQuestion
} from "shared/types";
import { ApplicationFee } from "types/landlord/verificationTemplate";

export interface TemplateAvailabilityRule {
  id: number;
  subject_type: string;
  subject_id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
}

export interface ReplaceTemplateAvailabilityPayload {
  availabilities: Omit<
    TemplateAvailabilityRule,
    "id" | "subject_type" | "subject_id"
  >[];
}

export interface TemplateTimeWindow {
  start_time: string;
  end_time: string;
}

export interface TemplateDateOverride {
  id: number;
  subject_type: string;
  subject_id: number;
  override_date: string;
  unavailable_all_day: boolean;
  start_time?: string | null;
  end_time?: string | null;
  created_at: string;
}

export interface SetTemplateDateOverridePayload {
  override_date: string;
  unavailable_all_day?: boolean;
  windows?: TemplateTimeWindow[];
}

export interface RemoveTemplateDateOverridePayload {
  override_date: string;
}

export type RentDisplayMode = "both" | "home" | "site" | "none";

export enum IdentityVerificationMode {
  Plaid = "plaid",
  Persona = "persona",
  ManualUpload = "manual_upload"
}

type IdentityVerification = {
  mode: IdentityVerificationMode;
  price: ApplicationFee;
};
type ServicesFee = {
  [ListingSchemaView.SelfReportedPrescreening]: ApplicationFee;
  [ListingSchemaView.IdentityVerification]: IdentityVerification[];
  [ListingSchemaView.ShowingScheduling]: ApplicationFee;
};

export type SecurityDepositKind = "first_month_rent" | "amount";

export type AdditionalFees = {
  application_fee: {
    enabled: boolean;
    amount: ApplicationFee | null;
  };
  security_deposit: {
    enabled: boolean;
    kind: SecurityDepositKind | null;
    amount: ApplicationFee | null;
  };
  other_fees: {
    enabled: boolean;
    text: string | null;
  };
};
export type PartnerUser = {
  id: number;
  name: string;
  email: string;
};

export type PrescreeningQuestionPayload = Omit<
  PrescreeningQuestion,
  "id" | "order" | "type"
> & {
  // Optional: omit for new questions, send back the BE-assigned id for
  // existing ones so they stay stable across saves (criteria conditions
  // reference questions by id — regenerating breaks them).
  id?: string;
};
export interface PrescreeningCriteriaCondition {
  field: "credit_score" | "gross_monthly_income" | "custom_question";
  operator: string;
  value?: string | number;
  question_id?: string;
}

export interface PrescreeningCriteriaRule {
  id: string;
  conditions: PrescreeningCriteriaCondition[];
  ui_group_id?: string;
  ui_operator?: "in" | "not_in";
}

export interface PrescreeningCriteria {
  schema_version?: number;
  enabled: boolean;
  decline: PrescreeningCriteriaRule[];
  review: PrescreeningCriteriaRule[];
  accept: PrescreeningCriteriaRule[];
}

export type PrescreeningTemplate = ListingButtons & {
  id: number;
  identifier: string;
  name: string;
  version: number;
  default: boolean;
  active: boolean;
  credit_score_enabled: boolean;
  credit_score_input_mode: PrescreeningMode | null;
  income_enabled: boolean;
  income_input_mode: PrescreeningMode | null;
  prescreening_questions: PrescreeningQuestion[];
  identity_verification_enabled: boolean;
  identity_verification_mode: IdentityVerificationMode;
  selfie_check_enabled: boolean;
  allow_schedule_on_idv_fail: boolean;
  require_new_idv: boolean;
  self_reported_prescreening_enabled: boolean;
  showing_scheduling_enabled: boolean;
  showing_type: "guided" | "self_guided" | null;
  custom_question_enabled: boolean;
  rent_display_mode: RentDisplayMode | null;
  listing_price_primary_display: ListingPricePrimaryDisplay | null;
  listing_price_secondary_display: ListingPriceSecondaryDisplay | null;
  entry_instructions: string | null;
  exit_instructions: string | null;
  created_at: string;
  updated_at: string;
  last_modified_by: PartnerUser;
  last_modified_at: string;
  created_by: PartnerUser;
  fee: ApplicationFee;
  service_fees: ServicesFee;
  prescreening_criteria?: PrescreeningCriteria;
  additional_fees: AdditionalFees;
  application_fee_enabled: boolean;
  application_fee_cents: number | null;
  security_deposit_enabled: boolean;
  security_deposit_kind: SecurityDepositKind | null;
  security_deposit_cents: number | null;
  other_fees_enabled: boolean;
  other_fees_text: string | null;
  availabilities?: Omit<
    TemplateAvailabilityRule,
    "id" | "subject_type" | "subject_id"
  >[];
  date_overrides?: SetTemplateDateOverridePayload[];
};

export type PrescreeningTemplatePayload = Partial<
  Omit<
    PrescreeningTemplate,
    | "id"
    | "identifier"
    | "version"
    | "default"
    | "active"
    | "created_at"
    | "updated_at"
    | "last_modified_by"
    | "last_modified_at"
    | "created_by"
    | "prescreening_questions"
  >
> & {
  prescreening_questions?: PrescreeningQuestionPayload[];
};

export interface PrescreeningRange {
  min: number;
  max: number | null;
  label: string;
}

export interface RangesResponse {
  credit_score_ranges: PrescreeningRange[];
  income_ranges: PrescreeningRange[];
}

export type PrescreeningCriteriaUsageQuery =
  | { question_id: string }
  | { field: "credit_score" | "gross_monthly_income" };

export interface PrescreeningCriteriaUsageResponse {
  in_use: boolean;
  affected_rules: Array<{
    section: "decline" | "review" | "accept";
    rule_id: string;
  }>;
}

export interface GetPrescreeningTemplatesResponse {
  items: PrescreeningTemplate[];
}
